import React, { useState } from 'react';
import {
  XMarkIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { insertDocument } from '../../utility/firebaseHelpers';
import { serverTimestamp } from 'firebase/firestore';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';

interface ReportListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  listingId: string;
  propertyTitle: string;
  currentUserId?: string;
}

const REPORT_CATEGORIES = [
  { value: 'fraud', label: 'Fraud/Scam' },
  {
    value: 'discrimination',
    label: 'Discrimination (e.g., No HAP, No Children)'
  },
  { value: 'illegal_rent', label: 'Illegal Rent (RPZ Violation)' },
  { value: 'missing_ber', label: 'Missing or Invalid BER Rating' },
  { value: 'misleading', label: 'Misleading Information' },
  { value: 'duplicate', label: 'Duplicate Listing' },
  { value: 'other', label: 'Other Illegal Content' }
];

const ReportListingModal: React.FC<ReportListingModalProps> = ({
  isOpen,
  onClose,
  listingId,
  propertyTitle,
  currentUserId
}) => {
  const [reportCategory, setReportCategory] = useState('');
  const [reporterName, setReporterName] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [goodFaithConfirmation, setGoodFaithConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Validation
    if (!reportCategory) {
      setSubmitError('Please select a reason for reporting.');
      return;
    }

    if (!reporterName.trim()) {
      setSubmitError('Please provide your name.');
      return;
    }

    if (!reporterEmail.trim() || !isValidEmail(reporterEmail)) {
      setSubmitError('Please provide a valid email address.');
      return;
    }

    if (!reportDetails.trim()) {
      setSubmitError('Please provide details about your report.');
      return;
    }

    if (!goodFaithConfirmation) {
      setSubmitError(
        'You must confirm that your report is made in good faith.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const reportData = {
        listingId,
        propertyTitle,
        reporterId: currentUserId || 'guest',
        reportCategory,
        reporterName,
        reporterEmail,
        reportDetails,
        goodFaithConfirmation,
        timestamp: serverTimestamp(),
        status: 'pending'
      };

      // Insert report into Firestore
      await insertDocument('listingReports', reportData);

      // Send confirmation email via Cloud Function
      try {
        await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/sendReportConfirmationEmail`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            reporterName,
            reporterEmail,
            propertyTitle,
            reportCategory,
            listingId
          })
        });
      } catch (emailError) {
        console.error('Error sending confirmation email:', emailError);
        // Don't fail the report submission if email fails
      }

      setSubmitSuccess(true);

      // Auto-close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('Error submitting report:', error);
      setSubmitError('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleClose = () => {
    setReportCategory('');
    setReporterName('');
    setReporterEmail('');
    setReportDetails('');
    setGoodFaithConfirmation(false);
    setSubmitError(null);
    setSubmitSuccess(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Report Listing
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Success Message */}
        {submitSuccess ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Report Submitted Successfully
            </h3>
            <p className="text-gray-600 mb-4">
              Thank you for reporting this listing. We have sent a confirmation
              email to <strong>{reporterEmail}</strong>.
            </p>
            <p className="text-sm text-gray-500">
              Our team will review your report and take appropriate action
              within 2-3 business days. You will be notified of our decision via
              email.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* DSA Notice */}
            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
              <p className="text-sm text-blue-800">
                <strong>Digital Services Act (DSA) Notice:</strong> This
                platform is committed to maintaining a safe and legal
                marketplace. Reports of illegal content will be reviewed in
                accordance with EU DSA requirements.
              </p>
            </div>

            {/* Property Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Reporting:</p>
              <p className="font-semibold text-gray-900">{propertyTitle}</p>
              <p className="text-xs text-gray-500 mt-1">
                Listing ID: {listingId}
              </p>
            </div>

            {/* Report Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Report <span className="text-red-500">*</span>
              </label>
              <select
                value={reportCategory}
                onChange={(e) => setReportCategory(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                disabled={isSubmitting}
                required
              >
                <option value="">Select a reason...</option>
                {REPORT_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Reporter Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={reporterName}
                onChange={(e) => setReporterName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="John Doe"
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Reporter Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={reporterEmail}
                onChange={(e) => setReporterEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="john.doe@example.com"
                disabled={isSubmitting}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                We'll use this to confirm receipt and notify you of our
                decision.
              </p>
            </div>

            {/* Report Details */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Details <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Please provide specific details about why you're reporting this listing. Include any relevant evidence or context."
                disabled={isSubmitting}
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Minimum 20 characters. Be as specific as possible.
              </p>
            </div>

            {/* Good Faith Confirmation */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={goodFaithConfirmation}
                  onChange={(e) => setGoodFaithConfirmation(e.target.checked)}
                  className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  disabled={isSubmitting}
                  required
                />
                <span className="text-sm text-gray-700">
                  <strong className="font-semibold">
                    Good Faith Declaration:
                  </strong>{' '}
                  I confirm that I have a good faith belief that the information
                  provided in this report is accurate and that this content
                  violates applicable laws or platform policies. I understand
                  that filing a false report may result in legal consequences.{' '}
                  <span className="text-red-500">*</span>
                </span>
              </label>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-3" />
                  <p className="text-sm text-red-700">{submitError}</p>
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !goodFaithConfirmation}
                className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  isSubmitting || !goodFaithConfirmation
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Submit Report'
                )}
              </button>
            </div>

            {/* Privacy Notice */}
            <p className="text-xs text-gray-500 text-center pt-4 border-t border-gray-200">
              Your report will be handled in accordance with our{' '}
              <a
                href="/privacy-policy"
                className="text-blue-600 hover:underline"
                target="_blank"
              >
                Privacy Policy
              </a>{' '}
              and EU DSA requirements. False or malicious reports may result in
              account suspension.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ReportListingModal;
